import React, { useEffect, useState } from "react";
import {
  useTranslation } from "react-i18next";
import {
    Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import TextField from './SearchableTextField';

import AddIcon from "@mui/icons-material/Add";
import { getAllClienti, getAllCompanies } from "../APIs/ClientsAPI";
import { createInvoice } from "../APIs/InvoicesAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import { getTenantSettings } from "../APIs/TenantSettingsAPI";
import { formattaFloatFatture } from "../utils/globalUtils";

const NATURA_IVA_MAP = [ // da rimuovere lo split e editare
    "N1 - Escluse ex art. 15",
    "N2.1 - Non soggette ad IVA ai sensi degli artt. da 7 a 7-septies del DPR 633/72",
    "N2.2 - Non soggette - altri casi",
    "N3.1 - Non imponibili - esportazioni",
    "N3.2 - Non imponibili - cessioni intracomunitarie",
    "N3.3 - Non imponibili - cessioni verso San Marino",
    "N3.4 - Non imponibili - operazioni assimilate alle cessioni all'esportazione",
    "N3.5 - Non imponibili - a seguito di dichiarazioni d'intento",
    "N3.6 - Non imponibili - altre operazioni che non concorrono alla formazione del plafond",
    "N4 - Esenti",
    "N5 - Regime del margine / IVA non esposta in fattura",
    "N6.1 - Inversione contabile - cessione di rottami e altri materiali di recupero",
    "N6.2 - Inversione contabile - cessione di oro e argento puro",
    "N6.3 - Inversione contabile - subappalto nel settore edile",
    "N6.4 - Inversione contabile - cessione di fabbricati",
    "N6.5 - Inversione contabile - cessione di telefoni cellulari",
    "N6.6 - Inversione contabile - cessione di prodotti elettronici",
    "N6.7 - Inversione contabile - prestazioni comparto edile e settori connessi",
    "N6.8 - Inversione contabile - operazioni settore energetico",
    "N6.9 - Inversione contabile - altri casi",
    "N7 - IVA assolta in altro stato UE",
];

export default function FatturazioneDialog({
    openCreateDialog,
    onCloseCreateDialog,
    setOpenRiepilogo,
    onInvoiceCreated,
    prezzoTotale,
    colors,
    isDark,
    preselectedClientId,
    preselectedCompanyId,
    ordiniFiltrati,
    linkedOrderIds = [],
}) {
    const [clients, setClients] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [createLoading, setCreateLoading] = useState(false);
    const [defaultVatRate, setDefaultVatRate] = useState(10);
    const [missingBillingOwnerFields, setMissingBillingOwnerFields] = useState([]);
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isRestaurantInvoice = String(localStorage.getItem("tenantType") || "").trim().toUpperCase() === "RISTORANTE";
    const aliquotaIniziale = defaultVatRate;
    const totaleSenzaIva = prezzoTotale
        ? ((prezzoTotale / (1 + aliquotaIniziale / 100)))
        : 0;

    const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

    const idsMatch = (left, right) => String(left || "") === String(right || "");

    const buildInitialInvoiceLines = () => {
        if (ordiniFiltrati) {
            const lines = ordiniFiltrati.map((ordine, index) => {
                    const product = ordine?.product || {};
                    const packageItem = ordine?.package || {};
                    const maxVat = getMaxVAT(product.categories)
                    const effectiveVat = maxVat ?? aliquotaIniziale;
                    const unitPrice = Number(
                        ordine?.unitPrice ??
                        ordine?.finalPrice ??
                        product.price ??
                        packageItem.price ??
                        0
                    );
                    return {
                        numero_linea: index + 1,
                        descrizione: product.name || packageItem.name || ordine?.name || "Riga ordine",
                        quantita: Number(ordine?.quantity || 1),
                        unita_misura: product.pricePerWeight ? (product.typeOfWeight === "HECTOGRAM" ? ("hg") : ("Kg")) : "pz",
                        prezzo_unitario: unitPrice,
                        sconto_maggiorazione_json: 0,
                        prezzo_totale: ((unitPrice / (1 + effectiveVat / 100))) || 0,
                        aliquota_iva: effectiveVat,
                        natura_iva: "",
                    }
                });

            const targetTotalCents = Math.max(0, Math.round(Number(prezzoTotale || 0) * 100));
            const grossAmounts = lines.map((line) => Math.max(
                0,
                Math.round(Number(line.prezzo_unitario || 0) * Number(line.quantita || 0) * 100)
            ));
            const grossTotalCents = grossAmounts.reduce((sum, amount) => sum + amount, 0);
            const adjustmentCents = targetTotalCents - grossTotalCents;
            let assignedCents = 0;

            return lines.map((line, index) => {
                const quantity = Math.max(1, Number(line.quantita || 1));
                const lineAdjustmentCents = index === lines.length - 1
                    ? adjustmentCents - assignedCents
                    : Math.round(adjustmentCents * (
                        grossTotalCents > 0 ? grossAmounts[index] / grossTotalCents : 1 / lines.length
                    ));
                assignedCents += lineAdjustmentCents;

                return {
                    ...line,
                    sconto_maggiorazione_json: Number(
                        ((lineAdjustmentCents / 100) / quantity).toFixed(5)
                    ),
                };
            });
        }

        return [{
            numero_linea: 1,
            descrizione: "Somministrazione alimenti e bevande",
            quantita: 1,
            unita_misura: "pz",
            prezzo_unitario: prezzoTotale,
            sconto_maggiorazione_json: 0,
            prezzo_totale: totaleSenzaIva,
            aliquota_iva: aliquotaIniziale,
            natura_iva: "",
        }];
    };

    const calculateInvoiceTotals = (lines = []) => {
        if (!Array.isArray(lines) || lines.length === 0) {
            return {
                imponibile_importo: formattaFloatFatture(0),
                imposta: formattaFloatFatture(0),
                totaleDocumento: formattaFloatFatture(0),
                importo_pagamento: formattaFloatFatture(0),
            };
        }

        const aliquote = {};
        lines.forEach((linea) => {
            const aliquota = Number(linea.aliquota_iva) || 0;
            const qty = Number(linea.quantita) || 0;
            const unitAdjustment = Number(linea.sconto_maggiorazione_json) || 0;
            const lordo = Math.max(0, (
                (Number(linea.prezzo_unitario) || 0) + unitAdjustment
            ) * qty);
            const key = aliquota.toFixed(2);
            aliquote[key] = aliquote[key] || { aliquota, lordo: 0 };
            aliquote[key].lordo += lordo;
        });

        let totaleImponibileCents = 0;
        let totaleImpostaCents = 0;
        let totaleDocumentoCents = 0;

        Object.values(aliquote).forEach(({ aliquota, lordo }) => {
            const lordoCents = Math.round(lordo * 100);
            const imponibile5dec = Math.round(lordo / (1 + aliquota / 100) * 100000) / 100000;
            const imponibileCents = Math.round(imponibile5dec * 100);

            totaleImponibileCents += imponibileCents;
            totaleImpostaCents += lordoCents - imponibileCents;
            totaleDocumentoCents += lordoCents;
        });

        const totaleImponibile = totaleImponibileCents / 100;
        const totaleImposta = totaleImpostaCents / 100;
        const totaleDocumento = totaleDocumentoCents / 100;

        return {
            imponibile_importo: formattaFloatFatture(totaleImponibile),
            imposta: formattaFloatFatture(totaleImposta),
            totaleDocumento: formattaFloatFatture(totaleDocumento),
            importo_pagamento: formattaFloatFatture(totaleDocumento),
        };
    };

    const getClientLabel = (client) => {
        if (!client) return "";
        const surname = (client.surname || "").trim();
        const name = (client.name || "").trim();
        return surname && surname !== name ? `${name} ${surname}` : name;
    };

    const initialInvoiceLines = buildInitialInvoiceLines();
    const initialInvoiceTotals = calculateInvoiceTotals(initialInvoiceLines);

    const initialCreateForm = {
        tipoDocumento: "TD01",
        divisa: "EUR",
        causale: "",
        statoFattura: "BOZZA",
        xmlVersion: "FPR12",

        denominazione: "",
        pec: "",
        clienteId: "",
        companyId: "",

        partitaIVA: "",
        codFiscaleCedente: "",
        indirizzoCedente: "",
        comuneCedente: "",
        capCedente: "",
        provinciaCedente: "",
        nazioneCedente: "",
        regimeFiscale: "RF01",
        rea_ufficio: "",
        rea_numero: "",
        capitale_sociale: "",
        socio_unico: true,
        stato_liquidazione: "LN",

        tipo_soggetto: "PRIVATO",
        id_paese: "",
        partita_iva: "",
        codFiscaleCliente: "",
        indirizzoCliente: "",
        numeroCivicoCliente: "",
        capCliente: "",
        comuneCliente: "",
        provinciaCliente: "",
        nazioneCliente: "",
        codice_destinatario: "",

        aliquota_iva: String(aliquotaIniziale),
        natura_iva: "",
        imponibile_importo: initialInvoiceTotals.imponibile_importo,
        imposta: initialInvoiceTotals.imposta,
        totaleDocumento: initialInvoiceTotals.totaleDocumento,
        esigibilita_iva: "I",

        condizioni_pagamento: "TP03",
        modalita_pagamento: "MP05",
        data_scadenza: new Date().toISOString().slice(0, 10),
        importo_pagamento: initialInvoiceTotals.importo_pagamento,
        iban: "",
        beneficiario: "",
        istituto_finanziario: "",

        articolo73: false,
        bolloVirtuale: false,
        importoBollo: "0",
        arrotondamento: "0",
        riferimento_normativo: "",

        linee: initialInvoiceLines,
    };

    const [createForm, setCreateForm] = useState(initialCreateForm);

    useEffect(() => {
        let ignore = false;

        async function fetchDefaultVatRate() {
            try {
                const token = localStorage.getItem("accessToken");
                const tenant = localStorage.getItem("tenantId");
                if (!token || !tenant) return;

                const settings = await getTenantSettings(token, tenant);
                const parsed = Number(String(settings?.defaultVatRate ?? 10).replace(",", "."));
                const missing = [
                    [settings?.ownerName, t("systemSettings.ownerName", { defaultValue: "ragione sociale/nome cedente" })],
                    [settings?.vatNumber, t("systemSettings.ownerVat", { defaultValue: "partita IVA cedente" })],
                    [settings?.address, t("systemSettings.ownerAddress", { defaultValue: "indirizzo cedente" })],
                    [settings?.cap, t("systemSettings.ownerPostalCode", { defaultValue: "CAP cedente" })],
                    [settings?.city, t("systemSettings.ownerCity", { defaultValue: "comune cedente" })],
                ]
                    .filter(([value]) => !String(value || "").trim())
                    .map(([, label]) => label);

                if (!ignore) setDefaultVatRate(Number.isFinite(parsed) ? parsed : 10);
                if (!ignore) setMissingBillingOwnerFields(missing);
            } catch (error) {
                console.warn("Errore recupero IVA predefinita tenant:", error);
                if (!ignore) setDefaultVatRate(10);
                if (!ignore) setMissingBillingOwnerFields([]);
            }
        }

        if (openCreateDialog) fetchDefaultVatRate();

        return () => {
            ignore = true;
        };
    }, [openCreateDialog, t]);

    useEffect(() => {
        if (openCreateDialog) {
            setCreateForm(initialCreateForm);
            if (isRestaurantInvoice && !preselectedClientId && !preselectedCompanyId) {
                setCreateForm(prev => ({
                    ...prev,
                    tipo_soggetto: "AZIENDA",
                }));
            }
            // Se è disponibile un companyId dalla prenotazione, preselezionalo come AZIENDA
            if (preselectedCompanyId) {
                setCreateForm(prev => ({
                    ...prev,
                    companyId: preselectedCompanyId,
                    tipo_soggetto: "AZIENDA",
                }));
            }
            // Altrimenti se è disponibile un clientId dalla prenotazione, preselezionalo come PRIVATO
            else if (preselectedClientId) {
                setCreateForm(prev => ({
                    ...prev,
                    clienteId: preselectedClientId,
                    tipo_soggetto: "PRIVATO",
                }));
            }
        }
    }, [openCreateDialog, preselectedClientId, preselectedCompanyId, aliquotaIniziale, isRestaurantInvoice]);

    const handleCloseDialog = () => {
        if (createLoading) return;
        setCreateForm(initialCreateForm);
        onCloseCreateDialog();
    };


    // Ricalcola automaticamente i totali quando cambiano le linee
    useEffect(() => {
        const totals = calculateInvoiceTotals(createForm.linee);

        setCreateForm(prev => ({
            ...prev,
            ...totals,
        }));
    }, [JSON.stringify(createForm.linee)]);

    useEffect(() => {
        async function fetchClients() {
            try {
                const token = localStorage.getItem("accessToken");
                const tenant = localStorage.getItem("tenantId");

                if (!token || !tenant) {
                    console.warn("Token o tenant non disponibili");
                    showSnackbar(t("billing.errors.missingAuth"), "error");
                    return;
                }

                const dataClients = await getAllClienti(token, tenant);
                const dataCompanies = await getAllCompanies(token, tenant);
                setClients(dataClients);
                setCompanies(dataCompanies);
            } catch (err) {
                console.error("Errore recupero clienti o aziende:", err);
                showSnackbar(
                    t("billing.errors.fetchClients") + ": " + err.message,
                    "error"
                );
            }
        }
        if (openCreateDialog) {
            fetchClients();
        }
    }, [openCreateDialog]);

    useEffect(() => {
        if (createForm.tipo_soggetto === "PRIVATO" && createForm.clienteId) {
            const client = clients.find(c => idsMatch(c.id, createForm.clienteId));
            if (client) {
                // Evita raddoppio del nome se surname è vuoto o uguale a name
                const fullName = getClientLabel(client);
                handleCreateFieldChange("denominazione", fullName);
                handleCreateFieldChange("pec", client.email || "");
                handleCreateFieldChange("indirizzoCliente", client.address || "");
                handleCreateFieldChange("numeroCivicoCliente", client.streetNumber || "");
                handleCreateFieldChange("capCliente", client.zipCode || "");
                handleCreateFieldChange("comuneCliente", client.city || "");
                handleCreateFieldChange("provinciaCliente", client.province || "");
                handleCreateFieldChange("codFiscaleCliente", client.taxCode || "");
                handleCreateFieldChange("partita_iva", client.vatNumber || "");
                handleCreateFieldChange("rea_numero", client.reaNumber || "");
                handleCreateFieldChange("rea_ufficio", client.reaOffice || "");
            }
        } else if (createForm.tipo_soggetto === "AZIENDA" && createForm.companyId) {
            const company = companies.find(c => idsMatch(c.id, createForm.companyId));
            if (company) {
                handleCreateFieldChange("denominazione", company.companyName || "");
                handleCreateFieldChange("pec", company.email || "");
                handleCreateFieldChange("partita_iva", company.vatNumber || "");
                handleCreateFieldChange("codFiscaleCliente", company.taxCode || "");
                handleCreateFieldChange("codice_destinatario", company.sdiCode || "");
                handleCreateFieldChange("indirizzoCliente", company.address || "");
                handleCreateFieldChange("numeroCivicoCliente", company.streetNumber || "");
                handleCreateFieldChange("capCliente", company.zipCode || "");
                handleCreateFieldChange("comuneCliente", company.city || "");
                handleCreateFieldChange("provinciaCliente", company.province || "");
                handleCreateFieldChange("rea_numero", company.reaNumber || "");
                handleCreateFieldChange("rea_ufficio", company.reaOffice || "");
            }
        }
    }, [createForm.clienteId, createForm.companyId, clients, companies]);

    const handleCreateFieldChange = (field, value) => {
        setCreateForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleRecipientEmailBlur = () => {
        if (createForm.tipo_soggetto !== "PRIVATO") return;

        const emailValue = String(createForm.pec || "").trim().toLowerCase();
        if (!emailValue) return;

        const existingClient = clients.find(
            (client) => String(client.email || "").trim().toLowerCase() === emailValue
        );

        if (existingClient && !idsMatch(existingClient.id, createForm.clienteId)) {
            handleCreateFieldChange("clienteId", existingClient.id);
            showSnackbar(
                `Email gia' presente: ho selezionato il cliente ${getClientLabel(existingClient) || existingClient.email}.`,
                "info"
            );
        }
    };

    const handleAddInvoiceLine = () => {
        setCreateForm((prev) => ({
            ...prev,
            linee: [
                ...prev.linee,
                {
                    numero_linea: prev.linee.length + 1,
                    descrizione: "",
                    quantita: 1,
                    unita_misura: "pz",
                    prezzo_unitario: 0,
                    prezzo_totale: 0,
                    sconto_maggiorazione_json: 0,
                    aliquota_iva: aliquotaIniziale,
                    natura_iva: "",
                },
            ],
        }));
    };

    const handleRemoveInvoiceLine = (index) => {
        setCreateForm((prev) => ({
            ...prev,
            linee: prev.linee
                .filter((_, i) => i !== index)
                .map((linea, i) => ({
                    ...linea,
                    numero_linea: i + 1,
                })),
        }));
    };

    const handleLineFieldChange = (index, field, value) => {
        setCreateForm((prev) => {
            const newLinee = [...prev.linee];
            newLinee[index] = {
                ...newLinee[index],
                [field]: value,
            };

            // Ricalcola il prezzo totale quando cambia prezzo, quantità, sconto o aliquota
            if (field === "prezzo_unitario" || field === "quantita" || field === "sconto_maggiorazione_json" || field === "aliquota_iva") {
                const prezzoUnitario = Number(
                    field === "prezzo_unitario" ? value : newLinee[index].prezzo_unitario
                ) || 0;
                const quantita = Number(
                    field === "quantita" ? value : newLinee[index].quantita
                ) || 0;
                const aliquota = Number(
                    field === "aliquota_iva" ? value : newLinee[index].aliquota_iva
                ) || 0;
                const scontoMaggiorazione = Number(
                    field === "sconto_maggiorazione_json" ? value : newLinee[index].sconto_maggiorazione_json
                ) || 0;

                // Calcola il totale della riga con IVA inclusa: PrezzoUnitario * Quantità
                const totaleRigaConIva = prezzoUnitario * quantita;
                // Converti in imponibile (prezzo senza IVA): dividi per (1 + IVA%)
                const imponibileOriginale = totaleRigaConIva / (1 + aliquota / 100);
                // Applica sconto/maggiorazione sull'imponibile (negativo per sconto, positivo per maggiorazione)
                const imponibileConSconto = imponibileOriginale
                    + (scontoMaggiorazione * quantita) / (1 + aliquota / 100);
                newLinee[index].prezzo_totale = (
                    imponibileConSconto
                );
            }

            return {
                ...prev,
                linee: newLinee,
            };
        });
    };

    const toPositiveIntOrUndefined = (value) => {
        if (value === null || value === undefined || value === "") return undefined;
        const parsed = Number(value);
        if (!Number.isInteger(parsed) || parsed <= 0) return undefined;
        return parsed;
    };

    const handleCreateInvoice = async (event) => {
        event?.preventDefault?.();

        try {
            setCreateLoading(true);

            const token = localStorage.getItem("accessToken");
            const tenant = localStorage.getItem("tenantId");
            if (!token || !tenant) {
                throw new Error("Sessione non valida. Rilogati e prova di nuovo.");
            }

            if (missingBillingOwnerFields.length > 0) {
                throw new Error(
                    `${t("billing.createDialog.incompleteBillingWarning", {
                        defaultValue: "Le informazioni del Cedente Prestatore non sono complete. Sistemale nella sezione Fatturazione delle impostazioni di sistema prima di generare una fattura.",
                    })} (${missingBillingOwnerFields.join(", ")})`
                );
            }

            const imponibile = Number(createForm.imponibile_importo || 0);
            const iva = Number(createForm.aliquota_iva || 0);
            const imposta =
                createForm.imposta !== ""
                    ? Number(createForm.imposta)
                    : (((imponibile * iva) / 100));

            const totaleDocumento =
                createForm.totaleDocumento !== ""
                    ? Number(createForm.totaleDocumento)
                    : ((imponibile + imposta));

            const importoPagamento =
                createForm.importo_pagamento !== ""
                    ? Number(createForm.importo_pagamento)
                    : totaleDocumento;

            const subjectType = createForm.tipo_soggetto || "PRIVATO";
            const denominazione = (createForm.denominazione || "").trim();

            if (!denominazione) {
                throw new Error(
                    subjectType === "AZIENDA"
                        ? "Inserisci la ragione sociale prima di creare la fattura."
                        : "Inserisci il nome del cliente prima di creare la fattura."
                );
            }

            if (isRestaurantInvoice) {
                if (!String(createForm.partita_iva || "").trim()) {
                    throw new Error("Inserisci la partita IVA del cliente.");
                }
                if (!String(createForm.indirizzoCliente || "").trim()) {
                    throw new Error("Inserisci l'indirizzo del cliente.");
                }
                if (
                    !String(createForm.pec || "").trim()
                    && !String(createForm.codice_destinatario || "").trim()
                ) {
                    throw new Error("Inserisci almeno uno tra PEC e codice SDI.");
                }
                if (!String(createForm.modalita_pagamento || "").trim()) {
                    throw new Error("Seleziona il metodo di pagamento.");
                }
                if (!(Number(createForm.totaleDocumento) > 0)) {
                    throw new Error("L'importo della fattura deve essere maggiore di zero.");
                }
            }

            const clienteId =
                subjectType === "PRIVATO"
                    ? toPositiveIntOrUndefined(createForm.clienteId)
                    : undefined;
            const companyId =
                subjectType === "AZIENDA"
                    ? toPositiveIntOrUndefined(createForm.companyId)
                    : undefined;
            const pecValue = createForm.pec || undefined;

            const invoiceLines = createForm.linee?.map((line) => {
                const quantity = Number(line.quantita || 0);
                const vatFactor = 1 + Number(line.aliquota_iva || 0) / 100;
                const grossUnitPrice = Number(line.prezzo_unitario || 0);
                const grossUnitAdjustment = Number(line.sconto_maggiorazione_json || 0);

                return {
                    numero_linea: line.numero_linea,
                    descrizione: line.descrizione,
                    quantita: quantity,
                    unita_misura: line.unita_misura,
                    prezzo_unitario: Number((grossUnitPrice / vatFactor).toFixed(5)),
                    sconto_maggiorazione_json: Number((grossUnitAdjustment / vatFactor).toFixed(5)),
                    prezzo_totale: Number(((grossUnitPrice + grossUnitAdjustment) * quantity).toFixed(5)),
                    aliquota_iva: Number(line.aliquota_iva || 0),
                    natura_iva: line.natura_iva,
                };
            });

            const invoicePayload = {
                orderIds: [...new Set([
                    ...(ordiniFiltrati || []).flatMap((row) => (
                        Array.isArray(row?.originalOrders) && row.originalOrders.length > 0
                            ? row.originalOrders.map((sourceRow) => Number(sourceRow?.orderId))
                            : [Number(row?.orderId)]
                    )),
                    ...(linkedOrderIds || []).map(Number),
                ].filter((id) => Number.isInteger(id) && id > 0))],
                tipoDocumento: createForm.tipoDocumento,
                divisa: createForm.divisa,
                causale: createForm.causale,
                articolo73: createForm.articolo73,
                bolloVirtuale: createForm.bolloVirtuale,
                importoBollo: Number(createForm.importoBollo || 0),
                arrotondamento: Number(createForm.arrotondamento || 0),
                statoFattura: createForm.statoFattura,
                xmlVersion: createForm.xmlVersion,

                partitaIVA: createForm.partitaIVA,
                codFiscaleCedente: createForm.codFiscaleCedente,
                indirizzoCedente: createForm.indirizzoCedente,
                comuneCedente: createForm.comuneCedente,
                capCedente: createForm.capCedente,
                provinciaCedente: createForm.provinciaCedente,
                nazioneCedente: createForm.nazioneCedente,
                denominazione,
                regimeFiscale: createForm.regimeFiscale,
                rea_ufficio: createForm.rea_ufficio,
                rea_numero: createForm.rea_numero,
                capitale_sociale: Number(createForm.capitale_sociale || 0),
                socio_unico: createForm.socio_unico,
                stato_liquidazione: createForm.stato_liquidazione,

                tipo_soggetto: subjectType,
                clienteId,
                companyId,
                id_paese: createForm.id_paese,
                partita_iva: createForm.partita_iva,
                codFiscaleCliente: createForm.codFiscaleCliente,
                indirizzoCliente: createForm.indirizzoCliente,
                numeroCivicoCliente: createForm.numeroCivicoCliente,
                capCliente: createForm.capCliente,
                comuneCliente: createForm.comuneCliente,
                provinciaCliente: createForm.provinciaCliente,
                nazioneCliente: createForm.nazioneCliente,
                codice_destinatario: createForm.codice_destinatario,
                pec: pecValue,

                aliquota_iva: iva,
                natura_iva: createForm.natura_iva,
                imponibile_importo: imponibile,
                imposta,
                totaleDocumento,
                esigibilita_iva: createForm.esigibilita_iva,
                riferimento_normativo: createForm.riferimento_normativo,

                condizioni_pagamento: createForm.condizioni_pagamento,
                modalita_pagamento: createForm.modalita_pagamento,
                data_scadenza: createForm.data_scadenza
                    ? new Date(createForm.data_scadenza).toISOString()
                    : undefined,
                importo_pagamento: importoPagamento,
                iban: createForm.iban,
                beneficiario: createForm.beneficiario,
                istituto_finanziario: createForm.istituto_finanziario,
                linee: invoiceLines
            };

            const createdInvoice = await createInvoice(invoicePayload, token, tenant);

            setCreateForm(initialCreateForm);
            showSnackbar(
                `Fattura creata come bozza`,
                "success"
            );

            // Notifica il parent che la fattura è stata creata
            if (onInvoiceCreated && createdInvoice) {
                onInvoiceCreated(createdInvoice);
                if (setOpenRiepilogo)
                    setOpenRiepilogo(true);
            }
        } catch (error) {
            console.error("Create invoice error:", error);
            showSnackbar(
                error?.message || "Errore durante la creazione della fattura",
                "error"
            );
        } finally {
            setCreateLoading(false);
            handleCloseDialog();
        }
    };

    function getMaxVAT(categorie) {
        if (!Array.isArray(categorie) || categorie.length < 1) return undefined;

        const rates = categorie
            .map((categoria) => categoria?.category?.vatRate)
            .filter((vatRate) => vatRate !== null && vatRate !== undefined && vatRate !== "")
            .map((vatRate) => Number(String(vatRate).replace(",", ".")))
            .filter((vatRate) => Number.isFinite(vatRate));

        if (rates.length < 1) return undefined;
        return Math.max(...rates);
    }

    return (
        <Dialog
            open={openCreateDialog}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>{t("billing.createDialog.title")}</DialogTitle>

            <DialogContent dividers>
                {isRestaurantInvoice ? (
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                I dati fiscali e le righe del conto vengono compilati automaticamente.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                autoComplete="organization"
                                label="Ragione sociale / Nome cliente"
                                value={createForm?.denominazione}
                                onChange={(e) => handleCreateFieldChange("denominazione", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                autoComplete="off"
                                label="Partita IVA"
                                value={createForm?.partita_iva}
                                onChange={(e) => handleCreateFieldChange("partita_iva", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                autoComplete="street-address"
                                label="Indirizzo"
                                value={createForm?.indirizzoCliente}
                                onChange={(e) => handleCreateFieldChange("indirizzoCliente", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                autoComplete="email"
                                label="PEC"
                                value={createForm?.pec}
                                onChange={(e) => handleCreateFieldChange("pec", e.target.value)}
                                onBlur={handleRecipientEmailBlur}
                                helperText="Compila la PEC oppure il codice SDI"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                autoComplete="off"
                                label="Codice SDI"
                                value={createForm?.codice_destinatario}
                                onChange={(e) => handleCreateFieldChange("codice_destinatario", e.target.value)}
                                helperText="Compila il codice SDI oppure la PEC"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                select
                                fullWidth
                                label="Metodo di pagamento"
                                value={createForm?.modalita_pagamento}
                                onChange={(e) => handleCreateFieldChange("modalita_pagamento", e.target.value)}
                            >
                                <MenuItem value="MP01">Contanti</MenuItem>
                                <MenuItem value="MP02">Assegno</MenuItem>
                                <MenuItem value="MP05">Bonifico</MenuItem>
                                <MenuItem value="MP08">Carta / POS</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                label="Importo fattura"
                                type="number"
                                value={createForm?.totaleDocumento}
                                InputProps={{ readOnly: true }}
                                helperText="Calcolato automaticamente dal conto"
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.subjectType")}
                            value={createForm?.tipo_soggetto}
                            onChange={(e) => handleCreateFieldChange("tipo_soggetto", e.target.value)}
                        >
                            <MenuItem value="PRIVATO">{t("billing.createDialog.fields.subjectTypeOptions.private")}</MenuItem>
                            <MenuItem value="AZIENDA">{t("billing.createDialog.fields.subjectTypeOptions.company")}</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.client")}
                            value={createForm?.clienteId || createForm?.companyId || ""}
                            onChange={
                                (e => {
                                    const value = e.target.value;
                                    if (createForm?.tipo_soggetto === "PRIVATO") {
                                        handleCreateFieldChange("clienteId", value);
                                        handleCreateFieldChange("companyId", "");
                                    } else {
                                        handleCreateFieldChange("companyId", value);
                                        handleCreateFieldChange("clienteId", "");
                                    }
                                })
                            }
                        >
                            {createForm?.tipo_soggetto === "PRIVATO" ? (
                                clients?.length > 0 ? (
                                    clients?.map(client => (
                                        <MenuItem key={client.id} value={client.id}>
                                            {client.name}{client.surname ? ` ${client.surname}` : ""}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled value="">{t("miscUi.noClientsAvailable")}</MenuItem>
                                )) : (
                                companies?.length > 0 ? (
                                    companies?.map(company => (
                                        <MenuItem key={company.id} value={company.id}>
                                            {company.companyName}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled value="">{t("miscUi.noCompaniesAvailable")}</MenuItem>
                                ))
                            }
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.customerName")}
                            value={createForm?.denominazione}
                            onChange={(e) => handleCreateFieldChange("denominazione", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.emailPec")}
                            value={createForm?.pec}
                            onChange={(e) => handleCreateFieldChange("pec", e.target.value)}
                            onBlur={handleRecipientEmailBlur}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.documentType")}
                            value={createForm?.tipoDocumento}
                            onChange={(e) => handleCreateFieldChange("tipoDocumento", e.target.value)}
                        >
                            <MenuItem value="TD01">TD01 - Fattura immediata</MenuItem>
                            <MenuItem value="TD24">TD24 - Fatt. differita</MenuItem>
                            <MenuItem value="TD25">TD25 - Fatt. differita (trangolazione)</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.customerAddress")}
                            value={createForm?.indirizzoCliente}
                            onChange={(e) => handleCreateFieldChange("indirizzoCliente", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.streetNumber")}
                            value={createForm?.numeroCivicoCliente}
                            onChange={(e) => handleCreateFieldChange("numeroCivicoCliente", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.zip")}
                            value={createForm?.capCliente}
                            onChange={(e) => handleCreateFieldChange("capCliente", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.city")}
                            value={createForm?.comuneCliente}
                            onChange={(e) => handleCreateFieldChange("comuneCliente", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.province")}
                            value={createForm?.provinciaCliente}
                            onChange={(e) => handleCreateFieldChange("provinciaCliente", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.taxCode")}
                            value={createForm?.codFiscaleCliente}
                            onChange={(e) => handleCreateFieldChange("codFiscaleCliente", e.target.value)}
                        />
                    </Grid>
                    {createForm?.tipo_soggetto === "AZIENDA" && (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                autoComplete="off"
                                label={t("billing.createDialog.fields.sdiCode")}
                                value={createForm?.codice_destinatario}
                                onChange={(e) => handleCreateFieldChange("codice_destinatario", e.target.value)}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.vatNumber")}
                            value={createForm?.partita_iva}
                            onChange={(e) => handleCreateFieldChange("partita_iva", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.reaOffice")}
                            value={createForm?.rea_ufficio}
                            onChange={(e) => handleCreateFieldChange("rea_ufficio", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.reaNumber")}
                            value={createForm?.rea_numero}
                            onChange={(e) => handleCreateFieldChange("rea_numero", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            fullWidth
                            autoComplete="off"
                            label="Socio unico"
                            value={createForm?.socio_unico ? "SI" : "NO"}
                            onChange={(e) => handleCreateFieldChange("socio_unico", e.target.value === "SI")}
                        >
                            <MenuItem value="SI">Sì</MenuItem>
                            <MenuItem value="NO">No</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            fullWidth
                            autoComplete="off"
                            label="Stato liquidazione"
                            value={createForm?.stato_liquidazione || ""}
                            onChange={(e) => handleCreateFieldChange("stato_liquidazione", e.target.value)}
                        >
                            <MenuItem value="LN">Non liquidato (LN)</MenuItem>
                            <MenuItem value="LS">Liquidato (LS)</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.taxableAmount")}
                            type="number"
                            value={createForm?.imponibile_importo}
                            onChange={(e) => handleCreateFieldChange("imponibile_importo", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.vatAmount")}
                            type="number"
                            value={createForm?.imposta}
                            onChange={(e) => handleCreateFieldChange("imposta", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.total")}
                            type="number"
                            value={createForm?.totaleDocumento}
                            onChange={(e) => handleCreateFieldChange("totaleDocumento", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.paymentConditions")}
                            value={createForm?.condizioni_pagamento}
                            onChange={(e) => handleCreateFieldChange("condizioni_pagamento", e.target.value)}
                        >
                            <MenuItem value="TP01">TP01 - Rate</MenuItem>
                            <MenuItem value="TP02">TP02 - Unico</MenuItem>
                            <MenuItem value="TP03">TP03 - Anticipo</MenuItem>
                            <MenuItem value="TP04">TP04 - Altro</MenuItem>
                            <MenuItem value="TP05">TP05 - Bonifico</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.paymentMethod")}
                            value={createForm?.modalita_pagamento}
                            onChange={(e) => handleCreateFieldChange("modalita_pagamento", e.target.value)}
                        >
                            <MenuItem value="MP01">MP01 - Cash</MenuItem>
                            <MenuItem value="MP05">MP05 - Bank transfer</MenuItem>
                            <MenuItem value="MP08">MP08 - Card</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.dueDate")}
                            type="date"
                            value={createForm?.data_scadenza}
                            onChange={(e) => handleCreateFieldChange("data_scadenza", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.paymentAmount")}
                            type="number"
                            value={createForm?.importo_pagamento}
                            onChange={(e) => handleCreateFieldChange("importo_pagamento", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={8}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            label={t("billing.createDialog.fields.reason")}
                            value={createForm?.causale}
                            onChange={(e) => handleCreateFieldChange("causale", e.target.value)}
                        />
                    </Grid>
                </Grid>
                {/* Linee Fattura */}
                <Grid container gap={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                        <Typography variant="h6">
                            {t("billing.createDialog.fields.invoiceLines")}
                        </Typography>
                    </Grid>
                    {createForm?.linee.map((line, index) => {
                        const scontoMaggiorazione = line.sconto_maggiorazione_json
                        return (
                            <Grid item xs={12} key={index} sx={{ p: 2, border: `1px solid ${colors.border}`, borderRadius: "8px", background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: "14px", color: colors.neutralMuted }}>
                                                #{index + 1}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            autoComplete="off"
                                            label={t("billing.createDialog.fields.invoiceLineDescription")}
                                            value={line.descrizione}
                                            onChange={(e) => handleLineFieldChange(index, "descrizione", e.target.value)}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            autoComplete="off"
                                            label={t("billing.createDialog.fields.invoiceLineQuantity")}
                                            type="number"
                                            value={line.quantita}
                                            onChange={(e) => handleLineFieldChange(index, "quantita", e.target.value)}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            autoComplete="off"
                                            label="U.M."
                                            value={line.unita_misura}
                                            onChange={(e) => handleLineFieldChange(index, "unita_misura", e.target.value)}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            autoComplete="off"
                                            label={t("billing.createDialog.fields.invoiceLineUnitPrice")}
                                            type="number"
                                            inputProps={{ step: "0.01" }}
                                            value={formattaFloatFatture(line.prezzo_unitario)}
                                            onChange={(e) => handleLineFieldChange(index, "prezzo_unitario", e.target.value)}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            autoComplete="off"
                                            label={t("billing.createDialog.fields.invoiceLineDiscountSurcharge")}
                                            value={line.sconto_maggiorazione_json}
                                            type="number"
                                            onChange={(e) => handleLineFieldChange(index, "sconto_maggiorazione_json", e.target.value)}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            autoComplete="off"
                                            label="IVA %"
                                            value={line.aliquota_iva}
                                            onChange={(e) => handleLineFieldChange(index, "aliquota_iva", e.target.value)}
                                            size="small"
                                        >
                                            <MenuItem value={0}>0%</MenuItem>
                                            <MenuItem value={4}>4%</MenuItem>
                                            <MenuItem value={5}>5%</MenuItem>
                                            <MenuItem value={10}>10%</MenuItem>
                                            <MenuItem value={22}>22%</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={2}> {/* campo per mostrare il valore dell'IVA */}
                                        <TextField
                                            fullWidth
                                            autoComplete="off"
                                            label="IVA Unit."
                                            type="number"
                                            value={formattaFloatFatture(line.prezzo_unitario - (line.prezzo_unitario / (1 + line.aliquota_iva / 100)))}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <Stack direction="row" spacing={1}>
                                            <TextField
                                                select
                                                fullWidth
                                                autoComplete="off"
                                                label="Natura IVA"
                                                value={line.natura_iva}
                                                onChange={(e) => handleLineFieldChange(index, "natura_iva", e.target.value)}
                                                size="small"
                                                disabled={Number(line.aliquota_iva) > 0}
                                            >
                                                <MenuItem value="">Nessuna</MenuItem>
                                                {NATURA_IVA_MAP.map((natura) => (
                                                    <MenuItem key={natura} value={natura}>
                                                        {natura}
                                                    </MenuItem>
                                                ))}
                                            </TextField>

                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => handleRemoveInvoiceLine(index)}
                                                sx={{ minWidth: 120 }}
                                            >
                                                {t("billing.createDialog.actions.removeLine") || "Rimuovi"}
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )
                    })}
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddInvoiceLine}
                        >
                            {t("billing.createDialog.actions.addInvoiceLine")}
                        </Button>
                    </Grid>
                </Grid>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    type="button"
                    onClick={handleCloseDialog}
                    disabled={createLoading}
                    sx={{ color: isDark ? "#FFFFFF" : undefined }}
                >
                    {t("billing.createDialog.actions.cancel")}
                </Button>
                <Button
                    type="button"
                    variant="contained"
                    onClick={handleCreateInvoice}
                    disabled={createLoading}
                    sx={{ color: isDark ? "#FFFFFF" : undefined }}
                >
                    {createLoading
                        ? t("billing.createDialog.actions.saving")
                        : t("billing.createDialog.actions.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

